import { PayNowOptionsQuery, SubscriptionPayment } from '@/generated/types';
import { LOCALE } from '@/utils/date';
import { formatPriceFromObject } from '@/utils/money';
import { FailedBannerFlow, FailedContract } from './FailedBanner';

export interface FailedBannerData {
  flow: FailedBannerFlow;
  contracts: FailedContract[];
  totalCount: number;
  totalPrice: string | null;
}

interface GetFailedBannerDataArgs {
  failedPayments: SubscriptionPayment[];
  failedTotalCount: number;
  failedTotalPrice: string | null;
  payNowOptions: PayNowOptionsQuery['payNowOptions'] | undefined;
  isPayNowAvailable: boolean;
  locale: LOCALE;
}

export const hasOldInfraDebt = (failedPayments: SubscriptionPayment[]): boolean =>
  failedPayments.some((payment) => !!payment.contract && payment.contract.__typename !== 'Flex');

export const getFailedBannerData = ({
  failedPayments,
  failedTotalCount,
  failedTotalPrice,
  payNowOptions,
  isPayNowAvailable,
  locale,
}: GetFailedBannerDataArgs): FailedBannerData | null => {
  const retryBannerData: FailedBannerData = {
    flow: FailedBannerFlow.retry,
    contracts: failedPayments.flatMap(({ contract }) =>
      contract
        ? [
            {
              id: contract.id,
              name: contract.__typename === 'Flex' ? contract.name : null,
              paymentMethodId: contract.paymentMethod?.id,
            },
          ]
        : []
    ),
    totalCount: failedTotalCount,
    totalPrice: failedTotalPrice,
  };

  // Old infra debt can only be settled through the retry flow, even when new infra debt exists.
  if (hasOldInfraDebt(failedPayments)) {
    return retryBannerData;
  }

  // Without pay now, every failed payment goes through the retry flow.
  if (!isPayNowAvailable) {
    return failedPayments.length > 0 ? retryBannerData : null;
  }

  // Options are sorted largest first, so the first one covers the full outstanding debt.
  const payNowOption = payNowOptions?.[0];

  if (!payNowOption?.resources.length) {
    return null;
  }

  return {
    flow: FailedBannerFlow.payNow,
    contracts: payNowOption.resources.flatMap(({ contract }) =>
      contract
        ? [
            {
              id: contract.id,
              name: contract.name,
              paymentMethodId: contract.currentPaymentMethod?.id,
            },
          ]
        : []
    ),
    totalCount: payNowOption.resources.length,
    totalPrice: formatPriceFromObject(
      { inCents: payNowOption.totalAmountInCents, currency: payNowOption.currency },
      locale
    ),
  };
};
